import { apiFetch, buildApiUrl } from "../../utils/apiFetch";
import { buildAssetUrl } from "../../utils/api";
import { getCookie } from "../../utils/cookies";

export const MAX_BANK_ACCOUNT_NUMBER_LENGTH = 18;
export const MAX_BANK_IFSC_CODE_LENGTH = 11;
export const MAX_BANK_ACCOUNT_HOLDER_LENGTH = 100;
export const MAX_BANK_NAME_LENGTH = 100;
export const MIN_UPI_ID_LENGTH = 3;
export const MAX_UPI_ID_LENGTH = 100;
export const MAX_OTHER_REASON_LENGTH = 500;

const BANK_NAME_PATTERN = /^[\p{L}][\p{L}\s.-]*$/u;
const UPI_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._-]*@[a-zA-Z0-9][a-zA-Z0-9._-]*$/;

export const getPersonNameValidationError = (value, label) => {
  const name = String(value ?? "").trim();
  if (name.length < 2 || name.length > MAX_BANK_ACCOUNT_HOLDER_LENGTH) {
    return `${label} must be 2 to ${MAX_BANK_ACCOUNT_HOLDER_LENGTH} characters.`;
  }
  if (!BANK_NAME_PATTERN.test(name)) {
    return `${label} can contain letters, spaces, periods, and hyphens only.`;
  }
  return null;
};

export const getUpiIdValidationError = (value) => {
  const upiId = String(value ?? "").trim();
  if (!upiId) return "Please enter your UPI ID.";
  if (upiId.length < MIN_UPI_ID_LENGTH || upiId.length > MAX_UPI_ID_LENGTH || !UPI_ID_PATTERN.test(upiId)) {
    return `UPI ID must be 3 to ${MAX_UPI_ID_LENGTH} characters in username@bankhandle format.`;
  }
  return null;
};

export const getBankTransferValidationError = ({
  bank_account_number,
  bank_ifsc_code,
  bank_account_holder,
  bank_name,
}) => {
  const accountNumber = String(bank_account_number ?? "").trim();
  const ifscCode = String(bank_ifsc_code ?? "").trim();
  const accountHolder = String(bank_account_holder ?? "").trim();
  const name = String(bank_name ?? "").trim();

  if (!accountNumber) return "Please enter your bank account number.";
  if (!/^\d{1,18}$/.test(accountNumber)) {
    return "Bank account number must contain 1 to 18 digits.";
  }
  if (!ifscCode) return "Please enter the IFSC code.";
  if (!/^[A-Z]{4}0[A-Z0-9]{6}$/i.test(ifscCode)) {
    return "IFSC code must be 4 letters, followed by 0, then 6 letters or digits.";
  }
  if (!accountHolder) return "Please enter the account holder name.";
  const accountHolderError = getPersonNameValidationError(accountHolder, "Account holder name");
  if (accountHolderError) return accountHolderError;
  if (name) {
    if (name.length < 2 || name.length > MAX_BANK_NAME_LENGTH) {
      return `Bank name must be 2 to ${MAX_BANK_NAME_LENGTH} characters.`;
    }
    if (!BANK_NAME_PATTERN.test(name)) {
      return "Bank name can contain letters, spaces, periods, and hyphens only.";
    }
  }
  return null;
};

// ─── Reason codes → display labels ──────────────────────────────────────────
export const RETURN_REASON_CODES = {
  DAMAGED_ITEM:  "Damaged Product",
  EXPIRED:       "Expired / Near Expiry",
  WRONG_ITEM:    "Wrong Item Delivered",
  MISSING_QTY:   "Missing Quantity",
  QUALITY_ISSUE: "Quality / Freshness Issue",
  OTHER:         "Other Reason",
};

export const RETURN_TYPE_OPTIONS = [
  { value: "RETURN", label: "Return & Refund" },
];

// ─── Refund preference options ────────────────────────────────────────────────
export const REFUND_PREFERENCE_OPTIONS = [
  { value: "WALLET",        label: "Customer Wallet",  description: "Instant credit to your KisanFresh wallet" },
  { value: "BANK_TRANSFER", label: "Bank Transfer",    description: "NEFT/RTGS to your bank account (2-3 days)" },
  { value: "UPI",           label: "UPI Transfer",     description: "Direct UPI transfer (usually instant)" },
];

// ─── Status badge config ─────────────────────────────────────────────────────
export const RETURN_STATUS_CONFIG = {
  UNDER_REVIEW:     { label: "Under Review",     bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   dot: "bg-amber-400"   },
  NEW:              { label: "Under Review",     bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   dot: "bg-amber-400"   },
  PENDING:          { label: "Pending Review",   bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   dot: "bg-amber-400"   },
  APPROVED:         { label: "Approved",         bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200",    dot: "bg-blue-500"    },
  PICKUP_SCHEDULED: { label: "Pickup Scheduled", bg: "bg-indigo-50",  text: "text-indigo-700",  border: "border-indigo-200",  dot: "bg-indigo-500"  },
  PROCESSING:       { label: "Processing",       bg: "bg-purple-50",  text: "text-purple-700",  border: "border-purple-200",  dot: "bg-purple-500"  },
  REFUNDED:         { label: "Refunded",         bg: "bg-green-50",   text: "text-green-700",   border: "border-green-200",   dot: "bg-green-500"   },
  REJECTED:         { label: "Rejected",         bg: "bg-red-50",     text: "text-red-700",     border: "border-red-200",     dot: "bg-red-400"     },
  COMPLETED:        { label: "Completed",        bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  CANCELLED:        { label: "Cancelled",        bg: "bg-gray-50",    text: "text-gray-600",    border: "border-gray-200",    dot: "bg-gray-400"    },
};

// ─── Normalize a return object from the real API shape ───────────────────────
// Real API response fields (from POST/GET /customer/orders/{id}/return):
//   return_request_id, return_request_number, status ("UNDER_REVIEW"), status_label ("Under Review"),
//   return_type ("REFUND"), requested_on, reasons [{code, label}], images [], items [],
//   order: { order_id }, timeline, current_status_info, pickup_details, refund_information
const normalizeReturn = (raw = {}) => ({
  ...raw,
  // IDs
  return_id:             raw.return_request_id    ?? raw.return_id    ?? raw.id,
  return_ref:            raw.return_request_number ?? raw.return_ref  ?? raw.reference ?? raw.return_number ?? null,
  // Order reference
  order_id:              raw.order?.order_id       ?? raw.order?.id ?? (typeof raw.order === "number" ? raw.order : null) ?? raw.order_id ?? raw.orderId ?? null,
  order_number:          raw.order?.order_number   ?? raw.order_number ?? (raw.order?.order_id ? `#${raw.order.order_id}` : null) ?? (raw.order?.id ? `#${raw.order.id}` : null) ?? (raw.order_id ? `#${raw.order_id}` : "") ?? "",
  // Status — use server's raw status for logic, ui_status for display fallback
  status:                normalizeReturnStatus(raw.status ?? raw.status_label ?? raw.ui_status),
  ui_status:             raw.status_label          ?? raw.ui_status             ?? null,
  status_label:          raw.status_label          ?? raw.ui_status             ?? null,
  // Return meta
  return_type:           raw.return_type           ?? null,
  return_type_label:     raw.return_type_label     ?? null,
  // reasons is [{code, label}] — normalise to both formats for easy use
  reasons:               Array.isArray(raw.reasons) ? raw.reasons : [],
  reason_codes:          Array.isArray(raw.reasons)
                           ? raw.reasons.map((r) => r.code)
                           : (Array.isArray(raw.reason_codes) ? raw.reason_codes : []),
  notes:                 raw.notes                 ?? raw.note ?? "",
  refund_amount:         parseFloat(raw.refund_information?.refund_amount ?? raw.refund_amount ?? raw.amount ?? 0),
  refund_preference:     raw.refund_preference     ?? raw.refund_information?.refund_preference ?? null,
  refund_information:    raw.refund_information    ?? null,
  pickup_details:        raw.pickup_details        ?? null,
  timeline:              raw.timeline              ?? null,
  current_status_info:   raw.current_status_info   ?? null,
  // Dates
  created_at:            raw.requested_on          ?? raw.requested_at          ?? raw.created_at ?? null,
  updated_at:            raw.updated_at             ?? raw.processed_at ?? null,
  // Product-level details from return request responses.
  return_items:          Array.isArray(raw.items) ? raw.items : (Array.isArray(raw.return_items) ? raw.return_items : []),
  items_to_be_returned:  raw.items_to_be_returned ?? (Array.isArray(raw.items)
    ? raw.items
        .map((item) => {
          const name = item.product_name ?? item.name ?? item.product?.name;
          return name ? `${name}${item.quantity != null ? ` (x${item.quantity})` : ""}` : null;
        })
        .filter(Boolean)
        .join(", ")
    : null),
  // Images
  images: (raw.images ?? []).map((img) =>
    typeof img === "string"
      ? { url: buildAssetUrl(img) }
      : { ...img, url: buildAssetUrl(img.url ?? img.path) }
  ),
});

const normalizeReturnStatus = (value) => {
  const status = String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  if (["NEW", "PENDING", "IN_REVIEW", "UNDER_REVIEW"].includes(status)) return "UNDER_REVIEW";
  if (["ASSIGNED", "PICKUPED", "PICKED_UP", "PICKUP_SCHEDULE", "PICKUP_SCHEDULED"].includes(status)) {
    return "PICKUP_SCHEDULED";
  }
  if (["DECLINED", "REJECT", "REJECTED"].includes(status)) return "REJECTED";
  return status;
};

// ─── multipart/form-data fetch ───────────────────────────────────────────────
// Use multipart only for requests that contain uploaded images.
const apiFetchForm = async (path, formData, method = "POST") => {
  const url = buildApiUrl(path);
  const token = getCookie("authToken");
  const headers = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  // DO NOT set Content-Type — browser sets it with the correct boundary for FormData

  const res = await fetch(url, { method, headers, body: formData });
  const json = await res.json();
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Request failed with status ${res.status}`);
  }
  return json;
};

// ─── Zustand slice ───────────────────────────────────────────────────────────
export const createReturnsSlice = (set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  returns:            [],
  returns_pagination: { page: 1, limit: 10, total: 0, hasMore: false },
  returns_loading:    false,
  returns_error:      null,
  returns_filter:     "ALL",
  active_return:      null,  // currently viewed single return
  order_proofs:       {},    // delivery proof cache, keyed by order_id

  setReturnsFilter:  (filter) => set({ returns_filter: filter }),
  clearActiveReturn: ()       => set({ active_return: null }),
  clearReturnsError: ()       => set({ returns_error: null }),

  // ── 1. List all returns (paginated) ────────────────────────────────────────
  // GET /api/v1/customer/returns?page=1&limit=10[&status=PENDING]
  fetchReturns: async ({ page = 1, limit = 10, status } = {}) => {
    set({ returns_loading: true, returns_error: null });
    try {
      const params = new URLSearchParams({ page, limit });
      const currentFilter = status ?? get().returns_filter;
      if (currentFilter && currentFilter !== "ALL") params.set("status", currentFilter);

      const json    = await apiFetch(`api/v1/customer/returns?${params}`);
      // Real API shape: { data: { returns: [...], total, page, limit } }
      const rawList = json.data?.returns ?? json.data?.data ?? json.data ?? [];
      const returns = Array.isArray(rawList) ? rawList.map(normalizeReturn) : [];

      const pagination = {
        page:    json.data?.page    ?? page,
        limit:   json.data?.limit   ?? limit,
        total:   json.data?.total   ?? returns.length,
        hasMore: (json.data?.page ?? page) * (json.data?.limit ?? limit) < (json.data?.total ?? returns.length),
      };

      set({ returns, returns_pagination: pagination, returns_loading: false });
      return returns;
    } catch (err) {
      set({ returns_error: err.message || "Failed to load returns", returns_loading: false });
      return [];
    }
  },

  // ── 2. Get single return by reference number ────────────────────────────────
  // GET /api/v1/customer/returns/{return_ref}  e.g. RR1234567890
  fetchReturnByRef: async (returnRef) => {
    set({ returns_loading: true, returns_error: null });
    try {
      const json       = await apiFetch(`api/v1/customer/returns/${returnRef}`);
      const returnData = normalizeReturn(json.data?.return ?? json.data ?? json);
      set({ active_return: returnData, returns_loading: false });
      return returnData;
    } catch (err) {
      set({ returns_error: err.message || "Failed to load return details", returns_loading: false });
      throw err;
    }
  },

  // ── 3. Get existing return for a specific order ─────────────────────────────
  // GET /api/v1/customer/orders/{order_id}/return
  fetchOrderReturn: async (orderId) => {
    set({ returns_loading: true, returns_error: null });
    try {
      const json       = await apiFetch(`api/v1/customer/orders/${orderId}/return`);
      const returnData = normalizeReturn(json.data?.return ?? json.data ?? json);
      set({ active_return: returnData, returns_loading: false });
      return returnData;
    } catch (err) {
      // 404 = no return exists for this order yet — treat as null, not an error
      const is404 =
        err.message?.includes("404") || err.message?.toLowerCase().includes("not found");
      set({ returns_loading: false, ...(!is404 && { returns_error: err.message }) });
      return null;
    }
  },

  // ── 4. Create a new return request (multipart/form-data) ───────────────────
  // POST /api/v1/customer/orders/{order_id}/return
  createReturnRequest: async ({
    order_id,
    items,                  // [{ order_item_id, quantity, remark }]
    return_type = "RETURN", // API now expects "RETURN"
    reason_codes,           // string[] e.g. ["DAMAGED_ITEM", "QUALITY_ISSUE"]
    notes = "",
    images = [],            // File[] from <input type="file" multiple>
    refund_preference = "WALLET", // "WALLET" | "BANK_TRANSFER" | "UPI"
    bank_account_number,    // required when refund_preference === "BANK_TRANSFER"
    bank_ifsc_code,         // required when refund_preference === "BANK_TRANSFER"
    bank_account_holder,    // required when refund_preference === "BANK_TRANSFER"
    bank_name,              // optional when refund_preference === "BANK_TRANSFER"
    upi_id,                 // required when refund_preference === "UPI"
  }) => {
    set({ returns_loading: true, returns_error: null });
    try {
      const itemsArr = Array.isArray(items)
        ? items
        : [{ order_item_id: items?.order_item_id, quantity: items?.quantity ?? 1 }];
      const normalizedItems = itemsArr.map((item) => ({
        order_item_id: item.order_item_id ?? item.id,
        quantity: Number(item.quantity ?? 1),
        ...(typeof item.remark === "string" && item.remark.trim() ? { remark: item.remark.trim() } : {}),
      }));
      if (!order_id) throw new Error("Order ID is required to create a return request.");
      if (!normalizedItems.length || normalizedItems.some((item) => !item.order_item_id || !Number.isInteger(item.quantity) || item.quantity < 1)) {
        throw new Error("Select at least one valid product and quantity for this return.");
      }
      const codesArr = Array.isArray(reason_codes) ? reason_codes : [reason_codes];

      // ── Build the refund-method-specific fields ──────────────────────────
      const refundFields = {};
      if (refund_preference === "BANK_TRANSFER") {
        const bankValidationError = getBankTransferValidationError({
          bank_account_number,
          bank_ifsc_code,
          bank_account_holder,
          bank_name,
        });
        if (bankValidationError) throw new Error(bankValidationError);
        if (bank_account_number) refundFields.bank_account_number = bank_account_number;
        if (bank_ifsc_code)      refundFields.bank_ifsc_code      = bank_ifsc_code;
        if (bank_account_holder) refundFields.bank_account_holder = bank_account_holder;
        if (bank_name)           refundFields.bank_name           = bank_name;
      } else if (refund_preference === "UPI") {
        const upiValidationError = getUpiIdValidationError(upi_id);
        if (upiValidationError) throw new Error(upiValidationError);
        refundFields.upi_id = String(upi_id).trim();
      }

      const path = `api/v1/customer/orders/${order_id}/return`;
      let json;
      if (images?.length) {
        const fd = new FormData();
        fd.append("items", JSON.stringify(normalizedItems));
        fd.append("return_type", return_type);
        fd.append("reason_codes", JSON.stringify(codesArr));
        fd.append("refund_preference", refund_preference);
        if (notes?.trim()) fd.append("notes", notes.trim());
        Object.entries(refundFields).forEach(([k, v]) => fd.append(k, v));
        images.forEach((file) => fd.append("images", file));
        json = await apiFetchForm(path, fd);
      } else {
        json = await apiFetch(path, {
          method: "POST",
          body: {
            items: normalizedItems,
            return_type,
            reason_codes: codesArr,
            refund_preference,
            ...(notes?.trim() ? { notes: notes.trim() } : {}),
            ...refundFields,
          },
        });
      }
      const returnData = normalizeReturn(json.data?.return ?? json.data ?? json);

      // Optimistically prepend to the list and set as active
      set((state) => ({
        returns:         [returnData, ...state.returns],
        active_return:   returnData,
        returns_loading: false,
      }));

      return returnData;
    } catch (err) {
      set({ returns_error: err.message || "Failed to create return request", returns_loading: false });
      throw err;
    }
  },

  // ── 5. Upload additional images to an existing return ───────────────────────
  // POST /api/v1/customer/returns/{return_id}/images
  //   --form 'images=@"/path/to/extra_photo.jpg"'
  uploadReturnImages: async (returnId, images = []) => {
    set({ returns_loading: true, returns_error: null });
    try {
      const fd = new FormData();
      images.forEach((file) => fd.append("images", file));

      const json    = await apiFetchForm(`api/v1/customer/returns/${returnId}/images`, fd);
      const updated = normalizeReturn(json.data?.return ?? json.data ?? {});

      set((state) => ({
        active_return:   state.active_return?.return_id === returnId ? updated : state.active_return,
        returns:         state.returns.map((r) =>
          r.return_id === returnId ? { ...r, images: updated.images } : r
        ),
        returns_loading: false,
      }));

      return updated;
    } catch (err) {
      set({ returns_error: err.message || "Failed to upload images", returns_loading: false });
      throw err;
    }
  },

  // ── 6. Get delivery proof for an order ─────────────────────────────────────
  // GET /api/v1/customer/orders/{order_id}/proof
  fetchOrderProof: async (orderId) => {
    set({ returns_loading: true, returns_error: null });
    try {
      const json = await apiFetch(`api/v1/customer/orders/${orderId}/proof`);
      const raw  = json.data?.proof ?? json.data ?? json;
      const proof = {
        ...raw,
        images: (raw.images ?? []).map((img) =>
          typeof img === "string"
            ? { url: buildAssetUrl(img) }
            : { ...img, url: buildAssetUrl(img.url ?? img.path) }
        ),
        delivered_at:  raw.delivered_at  ?? raw.created_at ?? null,
        receiver_name: raw.receiver_name ?? raw.name        ?? null,
        signature_url: buildAssetUrl(raw.signature_url ?? raw.signature ?? null),
      };
      set((state) => ({
        order_proofs:    { ...state.order_proofs, [orderId]: proof },
        returns_loading: false,
      }));
      return proof;
    } catch (err) {
      const is404 =
        err.message?.includes("404") || err.message?.toLowerCase().includes("not found");
      set({ returns_loading: false, ...(!is404 && { returns_error: err.message }) });
      return null;
    }
  },
});
